const registerWizard = (elementName) => {
    const aiGenId = elementName.slice('vacuum-bundle-wizard-'.length);

    if (customElements.get(elementName)) return;

    customElements.define(
      elementName,
      class extends HTMLElement {
        constructor() {
          super();

          this.currentStep = 1;
          this.pumpStage = 'question';
          this.returnToReview = false;
          this.baseFingerprint = '';
          this.baseSelection = '';
          this.cartVerified = false;
          this.moneyFormatter = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
          this.decimalMoneyFormatter = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2, maximumFractionDigits: 2 });
          this.journeyChoice = null;
          this.selectedPreset = null;
          this.selectedPump = null;
          this.extraPumpQuantities = {};
          this.bagQuantities = {};
          this.isSaving = false;
          this.cartMode = 'new';
          this.existingBundleId = null;
          this.existingLineKeys = [];
          this.isHydratingCart = true;
          this.isTransitioning = false;
          this.activeStepAnimation = null;
          this.activeHeightAnimation = null;
          this.feedbackTimer = null;
          this.prefersReducedMotion = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)'
          ).matches || false;
          this.animationsEnabled = true;
          this.selectionFeedbackDelay = 340;
          this.slideAnimationDuration = 290;
          this.editorAnimationDuration = 260;

          this.presets = {
            carryon: { '40x60': 5 },
            travel: { '40x60': 4, '50x70': 4 },
            home: { '60x80': 2, '80x100': 2 }
          };
        }

        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.lifecycle = new AbortController();
          this.isHydratingCart = true;
          this.isSaving = false;
          this.isTransitioning = false;
          this.i18n = JSON.parse(this.querySelector('[data-builder-translations]')?.textContent || '{}');

          this.publicProductId = this.dataset.publicProductId || '';
          this.pumpProductId =
            Number.parseInt(this.dataset.pumpProductId, 10) || 0;
          this.bagProductId =
            Number.parseInt(this.dataset.bagProductId, 10) || 0;
          this.installmentCount = Number.parseInt(this.dataset.installmentCount, 10) || 3;
          this.transferDiscount = Number.parseInt(this.dataset.transferDiscount, 10) || 20;
          this.maxPumpQuantity = Math.max(
            1,
            Number.parseInt(this.dataset.maxPumps, 10) || 3
          );
          this.addText = this.dataset.addText || 'Agregar mi combo al carrito';
          this.updateText = this.dataset.updateText || 'Actualizar mi combo';
          this.existingUpdateText =
            this.dataset.existingUpdateText || 'Actualizar pedido';
          this.savingText = this.dataset.savingText || 'Guardando...';

          this.animationsEnabled =
            this.dataset.animationsEnabled !== 'false';

          const configuredSelectionDuration =
            Number.parseInt(this.dataset.selectionDuration, 10);

          const configuredSlideDuration =
            Number.parseInt(this.dataset.slideDuration, 10);

          const configuredEditorDuration =
            Number.parseInt(this.dataset.editorDuration, 10);

          /*
           * La duración configurada controla la transición visual.
           * Solo esperamos una fracción breve antes de avanzar para que
           * el movimiento se perciba como respuesta, no como un delay.
           */
          this.selectionFeedbackDelay =
            this.animationsEnabled && !this.prefersReducedMotion
              ? Math.min(
                  180,
                  Math.round(
                    this.clampAnimationDuration(
                      configuredSelectionDuration,
                      0,
                      1200,
                      340
                    ) * 0.45
                  )
                )
              : 0;

          this.slideAnimationDuration =
            this.animationsEnabled && !this.prefersReducedMotion
              ? this.clampAnimationDuration(
                  configuredSlideDuration,
                  0,
                  1200,
                  290
                )
              : 0;

          this.editorAnimationDuration =
            this.animationsEnabled && !this.prefersReducedMotion
              ? this.clampAnimationDuration(
                  configuredEditorDuration,
                  0,
                  1200,
                  260
                )
              : 0;

          this.cartLoadingText = this.dataset.cartLoadingText || 'Revisando tu carrito...';
          this.cartFoundText = this.dataset.cartFoundText || 'Encontramos un combo en tu carrito. Podés modificarlo acá.';
          this.pumpStockMessage =
            this.dataset.pumpStockMessage ||
            'Este color está sin stock por ahora. Elegí el otro color.';
          this.bagStockMessage =
            this.dataset.bagStockMessage ||
            'Esta medida está sin stock por ahora. Elegí otro tamaño.';
          this.defaultErrorMessage = this.dataset.errorMessage || 'Completá los pasos para continuar.';
          this.root = window.Shopify?.routes?.root || '/';
          this.storageKey = `bmh:vacuum-builder:v1:${this.publicProductId}`;

          this.stepSections = Array.from(this.querySelectorAll('[data-step]'));
          this.wizardShell = this.querySelector('[data-wizard-shell]');
          this.progressTrack = this.querySelector('[data-progress-track]');
          this.progressLabel = this.querySelector('[data-progress-label]');
          this.progressElement = this.querySelector('[data-wizard-progress]');
          this.liveSummary = this.querySelector('[data-live-summary]');
          this.liveSummaryTitle = this.querySelector('[data-live-summary-title]');
          this.liveSummaryDetail = this.querySelector('[data-live-summary-detail]');
          this.existingEditor = this.querySelector('[data-existing-editor]');
          this.existingEditorTitle =
            this.querySelector('[data-existing-editor-title]');
          this.existingSaveButton =
            this.querySelector('[data-existing-save-button]');
          this.cartStatus = this.querySelector('[data-cart-status]');
          this.navigation = this.querySelector('[data-navigation]');
          this.backButton = this.querySelector('[data-back-button]');
          this.nextButton = this.querySelector('[data-next-button]');
          this.saveButton = this.querySelector('[data-save-button]');
          this.errorElement = this.querySelector('[data-error]');
          this.stockNotices =
            Array.from(this.querySelectorAll('[data-stock-notice]'));
          this.journeyButtons = Array.from(this.querySelectorAll('[data-journey-choice]'));
          this.pumpCards = Array.from(this.querySelectorAll('[data-pump-variant-id]'));
          this.extraPumpRows = Array.from(
            this.querySelectorAll('[data-extra-pump-variant-id]')
          );
          this.bagRows = Array.from(this.querySelectorAll('[data-bag-variant-id]'));
          this.reviewBagImages = Array.from(this.querySelectorAll('[data-review-bag-variant-id]'));
          this.editButtons = Array.from(this.querySelectorAll('[data-edit-step]'));
          this.reviewSummary =
            this.querySelector('[data-review-summary]');
          this.listPriceElements =
            Array.from(this.querySelectorAll('[data-list-price]'));
          this.transferPriceElements =
            Array.from(this.querySelectorAll('[data-transfer-price]'));
          this.savingsElements =
            Array.from(this.querySelectorAll('[data-savings-value]'));
          this.installmentPriceElements =
            Array.from(this.querySelectorAll('[data-installment-price]'));

          this.bagRowsByVariant = new Map();
          this.bagRows.forEach((row) => {
            const variantId = String(row.dataset.bagVariantId);
            const rows =
              this.bagRowsByVariant.get(variantId) || [];
            rows.push(row);
            this.bagRowsByVariant.set(variantId, rows);
          });

          this.extraPumpRowsByVariant = new Map();
          this.extraPumpRows.forEach((row) => {
            const variantId = String(row.dataset.extraPumpVariantId);
            const rows = this.extraPumpRowsByVariant.get(variantId) || [];
            rows.push(row);
            this.extraPumpRowsByVariant.set(variantId, rows);
          });

          this.setAttribute('aria-busy', 'true');
          this.initializeExtraPumpQuantities();
          this.initializeBagQuantities();
          this.setupEventListeners();
          window.addEventListener('pagehide', () => this.persistDraft(), { signal: this.lifecycle.signal });
          window.addEventListener('pageshow', (event) => {
            if (event.persisted) { this.isSaving = false; this.loadCartState(); }
          }, { signal: this.lifecycle.signal });
          document.addEventListener('bmh:cart-committed', (event) => this.handleCartCommitted(event.detail), { signal: this.lifecycle.signal });
          window.addEventListener('storage', (event) => {
            if (event.key === this.storageKey && event.newValue === null && event.oldValue !== null && !this.isSaving) this.resetAfterCartRemoval();
          }, { signal: this.lifecycle.signal });
          document.addEventListener('visibilitychange', () => {
            if (!document.hidden && (this.journeyChoice || this.cartMode !== 'new') && !this.isSaving && !this.isHydratingCart) this.loadCartState();
          }, { signal: this.lifecycle.signal });

          if (!this.pumpCards.length || !this.bagRows.length) {
            this.showError('Configurá el producto interno de bombas y el producto interno de bolsas desde el editor del tema.');
            this.isHydratingCart = false;
            this.removeAttribute('aria-busy');
            this.setLoadingState(false);
            return;
          }

          this.updatePresetPrices();
          this.updateAllExtraPumpDisplays();
          this.updateAllBagDisplays();
          this.updateInterface();
          this.loadCartState();
        }

        clampAnimationDuration(value, min, max, fallback) {
          const parsed = Number.parseInt(value, 10);

          if (!Number.isFinite(parsed)) {
            return fallback;
          }

          return Math.min(
            max,
            Math.max(min, parsed)
          );
        }

        normalizeTitle(value) {
          return String(value || '')
            .toLowerCase()
            .replace(/×/g, 'x')
            .replace(/\s+/g, '');
        }

        parseOptionalInteger(value) {
          if (value === undefined || value === null || value === '') return null;
          const parsed = Number.parseInt(value, 10);
          return Number.isFinite(parsed) ? parsed : null;
        }

        initializeBagQuantities() {
          this.bagRows.forEach((row) => {
            const variantId = String(row.dataset.bagVariantId);
            this.bagQuantities[variantId] = {
              quantity: 0,
              price: Number(row.dataset.variantPrice) || 0,
              name: row.dataset.variantTitle || '',
              available: row.dataset.variantAvailable !== 'false',
              maxQuantity: this.parseOptionalInteger(row.dataset.maxQuantity)
            };
          });
        }

        initializeExtraPumpQuantities() {
          this.extraPumpRows.forEach((row) => {
            const variantId = String(row.dataset.extraPumpVariantId);

            if (this.extraPumpQuantities[variantId]) return;

            this.extraPumpQuantities[variantId] = {
              quantity: 0,
              price: Number(row.dataset.variantPrice) || 0,
              name: row.dataset.variantTitle || '',
              available: row.dataset.variantAvailable !== 'false',
              maxQuantity: this.parseOptionalInteger(row.dataset.maxQuantity)
            };
          });
        }

        getTotalExtraPumpQuantity() {
          return Object.values(this.extraPumpQuantities).reduce(
            (total, data) => total + (Number(data.quantity) || 0),
            0
          );
        }

        getTotalPumpQuantity() {
          return this.selectedPump
            ? 1 + this.getTotalExtraPumpQuantity()
            : 0;
        }

        getExtraPumpStockLimit(variantId, data) {
          if (!data?.available) return 0;
          if (data.maxQuantity === null) return Number.POSITIVE_INFINITY;

          const primaryQuantity =
            String(this.selectedPump?.id || '') === String(variantId)
              ? 1
              : 0;

          return Math.max(0, data.maxQuantity - primaryQuantity);
        }

        normalizeExtraPumpQuantities() {
          let remaining = Math.max(0, this.maxPumpQuantity - 1);

          Object.entries(this.extraPumpQuantities).forEach(([variantId, data]) => {
            const stockLimit = this.getExtraPumpStockLimit(variantId, data);
            const normalized = Math.min(
              Math.max(0, Number.parseInt(data.quantity, 10) || 0),
              stockLimit,
              remaining
            );

            data.quantity = normalized;
            remaining -= normalized;
          });
        }

        setExtraPumpQuantity(variantId, quantity, update = true) {
          const data = this.extraPumpQuantities[variantId];
          if (!data) return;

          const otherQuantity = Object.entries(this.extraPumpQuantities).reduce(
            (total, [id, item]) =>
              id === variantId
                ? total
                : total + (Number(item.quantity) || 0),
            0
          );

          const bundleLimit = Math.max(
            0,
            this.maxPumpQuantity - 1 - otherQuantity
          );
          const stockLimit = this.getExtraPumpStockLimit(variantId, data);

          data.quantity = Math.min(
            Math.max(0, Number.parseInt(quantity, 10) || 0),
            bundleLimit,
            stockLimit
          );

          this.updateExtraPumpDisplays(variantId);

          if (update) {
            this.updateInterface();
            this.hideError();
          }
        }

        updateExtraPumpDisplays(variantId) {
          const data = this.extraPumpQuantities[variantId];
          if (!data) return;
          const quantity = this.getPumpTotalQuantity(variantId);
          const total = this.getTotalPumpQuantity();
          const atStockLimit = data.maxQuantity !== null && quantity >= data.maxQuantity;
          (this.extraPumpRowsByVariant.get(String(variantId)) || []).forEach((row) => {
            row.querySelector('[data-extra-pump-qty]').textContent = quantity;
            row.querySelector('[data-extra-pump-action="decrease"]').disabled = quantity === 0 || total <= 1;
            row.querySelector('[data-extra-pump-action="increase"]').disabled = !data.available || atStockLimit || total >= this.maxPumpQuantity;
            row.classList.toggle('has-quantity', quantity > 0);
          });
        }

        getPumpTotalQuantity(variantId) {
          return (this.extraPumpQuantities[variantId]?.quantity || 0) + (String(this.selectedPump?.id) === String(variantId) ? 1 : 0);
        }

        setPumpTotalQuantity(variantId, requested) {
          const data = this.extraPumpQuantities[variantId];
          if (!data) return;
          const current = this.getPumpTotalQuantity(variantId);
          const others = this.getTotalPumpQuantity() - current;
          const max = data.available ? Math.min(data.maxQuantity ?? Infinity, this.maxPumpQuantity - others) : current;
          const quantity = Math.max(0, Math.min(Math.floor(Number(requested) || 0), max));
          if (quantity + others < 1) return;
          const totals = Object.fromEntries(Object.keys(this.extraPumpQuantities).map(id => [id, this.getPumpTotalQuantity(id)]));
          totals[variantId] = quantity;
          if (!totals[this.selectedPump?.id]) {
            const nextId = Object.keys(totals).find(id => totals[id] > 0);
            this.selectedPump = { id: nextId, ...this.extraPumpQuantities[nextId] };
          }
          Object.entries(this.extraPumpQuantities).forEach(([id, item]) => {
            item.quantity = totals[id] - (id === String(this.selectedPump.id) ? 1 : 0);
          });
          this.pumpCards.forEach(card => {
            const selected = card.dataset.pumpVariantId === String(this.selectedPump.id);
            card.classList.toggle('selected', selected);
            card.setAttribute('aria-pressed', String(selected));
          });
          this.updateAllExtraPumpDisplays();
          this.updateInterface();
          this.hideError();
        }

        updateAllExtraPumpDisplays() {
          Object.keys(this.extraPumpQuantities).forEach((variantId) => {
            this.updateExtraPumpDisplays(variantId);
          });
        }

        areExtraPumpsValid() {
          if (this.getTotalPumpQuantity() > this.maxPumpQuantity) return false;

          return Object.entries(this.extraPumpQuantities).every(
            ([variantId, data]) =>
              data.quantity >= 0 &&
              (data.quantity === 0 || data.available) &&
              data.quantity <= this.getExtraPumpStockLimit(variantId, data)
          );
        }

        waitForNextPaint() {
          return new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(resolve);
            });
          });
        }

        async waitForSelectionFeedback(element) {
          if (
            !element ||
            this.prefersReducedMotion ||
            this.selectionFeedbackDelay <= 0
          ) {
            return;
          }

          /*
           * Two animation frames guarantee that the selected state
           * is painted before the confirmation movement begins.
           */
          await this.waitForNextPaint();

          element.classList.add('is-confirming');

          await new Promise((resolve) => {
            window.clearTimeout(this.feedbackTimer);

            this.feedbackTimer = window.setTimeout(() => {
              element.classList.remove('is-confirming');
              this.feedbackTimer = null;
              resolve();
            }, this.selectionFeedbackDelay);
          });
        }

        async confirmSelectionAndAdvance(element, targetStep) {
          if (this.isTransitioning) return;

          this.isTransitioning = true;

          try {
            await this.waitForSelectionFeedback(element);
            this.goToStep(targetStep);
          } finally {
            this.isTransitioning = false;
          }
        }

        showStockNotice(type, itemName = '') {
          const cleanName = String(itemName || '').trim();
          const message = type === 'pump'
            ? (
                cleanName
                  ? `El color ${cleanName} está sin stock por ahora. Elegí el otro color.`
                  : this.pumpStockMessage
              )
            : (
                cleanName
                  ? `Las bolsas ${cleanName} están sin stock por ahora. Elegí otro tamaño.`
                  : this.bagStockMessage
              );

          this.stockNotices.forEach((notice) => {
            if (notice.dataset.stockNotice !== type) return;
            notice.textContent = message;
            notice.hidden = false;
          });
        }

        hideStockNotice(type = null) {
          this.stockNotices.forEach((notice) => {
            if (type && notice.dataset.stockNotice !== type) return;
            notice.textContent = '';
            notice.hidden = true;
          });
        }

        setupEventListeners() {
          this.addEventListener('click', async (event) => {
            const button = event.target.closest('button');
            if (!button || !this.contains(button) || button.disabled || this.isHydratingCart || this.isSaving || this.isTransitioning) return;
            if (button.matches('[data-journey-choice]')) {
              if (this.chooseJourney(button.dataset.journeyChoice)) await this.confirmSelectionAndAdvance(button, 2);
            } else if (button.matches('[data-pump-variant-id]')) {
              if (button.dataset.variantAvailable === 'false') { this.showStockNotice('pump', button.dataset.variantTitle); return; }
              this.selectPump(button);
              this.pumpStage = this.getTotalPumpQuantity() > 1 ? 'quantities' : 'question';
              await this.confirmSelectionAndAdvance(button, this.maxPumpQuantity > 1 ? 3 : 4);
            } else if (button.matches('[data-pump-decision]')) {
              if (button.dataset.pumpDecision === 'yes') {
                this.pumpStage = 'quantities';
                this.goToStep(3);
              } else {
                Object.keys(this.extraPumpQuantities).forEach(id => { this.extraPumpQuantities[id].quantity = 0; });
                this.updateAllExtraPumpDisplays();
                this.goToStep(4);
              }
            } else if (button.matches('[data-extra-pump-action]')) {
              const id = button.closest('[data-extra-pump-variant-id]').dataset.extraPumpVariantId;
              this.setPumpTotalQuantity(id, this.getPumpTotalQuantity(id) + (button.dataset.extraPumpAction === 'increase' ? 1 : -1));
            } else if (button.matches('[data-action]')) {
              const row = button.closest('[data-bag-variant-id]');
              const id = row.dataset.bagVariantId;
              const bag = this.bagQuantities[id];
              if (!bag?.available) { this.showStockNotice('bag', bag?.name); return; }
              this.setBagQuantity(id, bag.quantity + (button.dataset.action === 'increase' ? 1 : -1));
              this.selectedPreset = this.matchCurrentPreset();
              this.journeyChoice = this.selectedPreset || 'custom';
              this.updateJourneyButtons();
              this.updateInterface();
              this.hideError();
            } else if (button.matches('[data-back-button]')) {
              if (this.returnToReview && this.isSelectionValid()) this.goToStep(5);
              else if (this.currentStep === 3 && this.pumpStage === 'quantities') { this.pumpStage = 'question'; this.goToStep(3); }
              else this.goToStep(Math.max(1, this.currentStep - 1));
            } else if (button.matches('[data-next-button]')) {
              this.advanceStep();
            } else if (button.matches('[data-save-button]')) {
              await this.saveSelection();
            } else if (button.matches('[data-edit-step]')) {
              this.editStep(Number(button.dataset.editStep));
            }
          }, { signal: this.lifecycle.signal });
        }

        selectPump(card, update = true) {
          if (!card || card.dataset.variantAvailable === 'false') return;

          const selectedVariantId =
            String(card.dataset.pumpVariantId);

          this.pumpCards.forEach((item) => {
            const selected =
              String(item.dataset.pumpVariantId) === selectedVariantId;

            item.classList.toggle('selected', selected);
            item.setAttribute(
              'aria-pressed',
              selected ? 'true' : 'false'
            );
          });

          this.selectedPump = {
            id: selectedVariantId,
            price: Number(card.dataset.variantPrice) || 0,
            name: card.dataset.variantTitle || '',
            available: card.dataset.variantAvailable !== 'false'
          };

          this.normalizeExtraPumpQuantities();
          this.updateAllExtraPumpDisplays();

          this.updatePresetPrices();
          this.hideStockNotice('pump');

          if (update) {
            this.updateInterface();
            this.hideError();
          }
        }

        chooseJourney(choice) {
          if (!choice) return false;

          if (choice === 'custom') {
            this.journeyChoice = choice;
            this.selectedPreset = null;
          } else {
            const applied = this.applyPreset(choice);

            if (!applied) {
              return false;
            }

            this.journeyChoice = choice;
            this.selectedPreset = choice;
          }

          this.updateJourneyButtons();
          this.updateInterface();
          this.hideError();

          return true;
        }

        applyPreset(presetName) {
          const preset = this.presets[presetName];
          if (!preset) return false;

          for (const [size, requestedQuantity] of Object.entries(preset)) {
            const entry = this.findBagBySize(size);
            if (!entry || !this.bagCanSupply(entry.data, requestedQuantity)) {
              const unavailableName =
                entry?.data?.name || size;

              this.showStockNotice(
                'bag',
                unavailableName
              );

              this.showError(
                `Las bolsas ${unavailableName} están sin stock por ahora. Elegí otro combo o armalo a tu gusto.`
              );
              return false;
            }
          }

          Object.keys(this.bagQuantities).forEach((variantId) => {
            this.setBagQuantity(variantId, 0, false);
          });

          Object.entries(preset).forEach(([size, quantity]) => {
            const entry = this.findBagBySize(size);
            if (entry) this.setBagQuantity(entry.id, quantity, false);
          });

          this.updateAllBagDisplays();
          return true;
        }

        updateJourneyButtons() {
          this.journeyButtons.forEach((button) => {
            const selected = button.dataset.journeyChoice === this.journeyChoice;
            button.classList.toggle('selected', selected);
            button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
        }

        bagCanSupply(data, requestedQuantity = 1) {
          if (!data?.available) return false;
          return data.maxQuantity === null || data.maxQuantity >= requestedQuantity;
        }

        findBagBySize(size) {
          const normalized = this.normalizeTitle(size);

          for (const [id, data] of Object.entries(this.bagQuantities)) {
            if (this.normalizeTitle(data.name).includes(normalized)) {
              return { id, data };
            }
          }

          return null;
        }

        setBagQuantity(variantId, quantity, updateDisplay = true) {
          const data = this.bagQuantities[variantId];
          if (!data || !data.available) return;

          let normalized = Math.max(0, Number.parseInt(quantity, 10) || 0);

          if (data.maxQuantity !== null) {
            normalized = Math.min(normalized, Math.max(0, data.maxQuantity));
          }

          data.quantity = normalized;
          this.hideStockNotice('bag');

          if (updateDisplay) {
            const rows =
              this.bagRowsByVariant.get(
                String(variantId)
              ) || [];

            rows.forEach((row) => {
              this.updateBagDisplay(row, variantId);
            });
          }
        }

        updateBagDisplay(row, variantId) {
          const data = this.bagQuantities[variantId];
          const value = row.querySelector('[data-qty-display]');
          const decrease = row.querySelector('[data-action="decrease"]');
          const increase = row.querySelector('[data-action="increase"]');

          if (value) value.textContent = data.quantity;
          row.classList.toggle('has-quantity', data.quantity > 0);

          if (decrease) {
            decrease.disabled = !data.available || data.quantity <= 0;
          }

          if (increase) {
            const atMaximum =
              data.maxQuantity !== null &&
              data.quantity >= data.maxQuantity;

            increase.disabled =
              data.available && atMaximum;

            increase.setAttribute(
              'aria-disabled',
              (!data.available || atMaximum)
                ? 'true'
                : 'false'
            );
          }
        }

        updateAllBagDisplays() {
          this.bagRows.forEach((row) => {
            this.updateBagDisplay(row, String(row.dataset.bagVariantId));
          });
        }

        matchCurrentPreset() {
          for (const [presetName, preset] of Object.entries(this.presets)) {
            let matches = true;

            for (const data of Object.values(this.bagQuantities)) {
              const expectedEntry = Object.entries(preset).find(([size]) => {
                return this.normalizeTitle(data.name).includes(this.normalizeTitle(size));
              });

              const expected = expectedEntry ? expectedEntry[1] : 0;
              if (data.quantity !== expected) {
                matches = false;
                break;
              }
            }

            if (matches) return presetName;
          }

          return null;
        }

        getPreviewPump() {
          if (this.selectedPump?.available) {
            return this.selectedPump;
          }

          const availableCard = this.pumpCards
            .filter((card) => card.dataset.variantAvailable !== 'false')
            .reduce((lowest, card) => !lowest || Number(card.dataset.variantPrice) < Number(lowest.dataset.variantPrice) ? card : lowest, null);

          if (!availableCard) return null;

          return {
            id: String(availableCard.dataset.pumpVariantId),
            price: Number(availableCard.dataset.variantPrice) || 0,
            name: availableCard.dataset.variantTitle || '',
            available: true
          };
        }

        calculatePresetTotal(presetName) {
          const preset = this.presets[presetName];
          const previewPump = this.getPreviewPump();
          if (!preset || !previewPump) return null;

          let total = previewPump.price;

          for (const [size, quantity] of Object.entries(preset)) {
            const entry = this.findBagBySize(size);
            if (!entry || !this.bagCanSupply(entry.data, quantity)) return null;
            total += entry.data.price * quantity;
          }

          return total;
        }

        calculateMinimumTotal() {
          const pump = this.getPreviewPump();
          const bags = Object.values(this.bagQuantities)
            .filter((data) => this.bagCanSupply(data, 1));
          if (!pump || !bags.length) return null;
          return pump.price + Math.min(...bags.map((data) => data.price));
        }

        updatePresetPrices() {
          this.journeyButtons.forEach((button) => {
            const presetName = button.dataset.journeyChoice;
            const price = button.querySelector('[data-preset-price]');
            if (!price) return;

            const total = presetName === 'custom' ? this.calculateMinimumTotal() : this.calculatePresetTotal(presetName);
            const unavailable = total === null;
            button.disabled = unavailable;
            button.hidden = unavailable;
            button.setAttribute(
              'aria-disabled',
              unavailable ? 'true' : 'false'
            );
            price.textContent =
              unavailable
                ? 'Sin stock en una medida'
                : (this.selectedPump ? this.formatMoney(total) : (this.i18n.price_from || '__price__').replace('__price__', this.formatMoney(total)));
          });
        }

        getTotalBagQuantity() {
          return Object.values(this.bagQuantities).reduce(
            (total, data) => total + data.quantity,
            0
          );
        }

        calculateTotalPrice() {
          if (!this.selectedPump) return 0;

          const pumpsTotal = Object.values(this.extraPumpQuantities).reduce(
            (total, data) => total + data.price * data.quantity,
            this.selectedPump.price
          );

          return Object.values(this.bagQuantities).reduce(
            (total, data) => total + data.price * data.quantity,
            pumpsTotal
          );
        }

        getPaymentBreakdown(total) {
          const transferPrice = Math.round(total * (100 - this.transferDiscount) / 100);

          const installmentPrice = Math.round(total / this.installmentCount);

          return {
            listPrice: total,
            transferPrice,
            savings: total - transferPrice,
            installmentPrice
          };
        }

        isSelectionValid() {
          return Boolean(
            this.selectedPump?.available &&
            this.getTotalBagQuantity() >= 1 &&
            this.areExtraPumpsValid() &&
            Object.values(this.bagQuantities).every((data) => {
              if (data.quantity < 1) return true;
              if (!data.available) return false;
              return data.maxQuantity === null || data.quantity <= data.maxQuantity;
            })
          );
        }

        isStepValid(step) {
          if (step === 1) return Boolean(this.journeyChoice);
          if (step === 2 || step === 3) return Boolean(this.selectedPump?.available) && this.areExtraPumpsValid();
          if (step === 4 || step === 5) return this.isSelectionValid();
          return false;
        }

        getStepError(step) {
          if (step === 1) return 'Elegí para qué querés usar el combo.';
          if (step === 2) return 'Elegí el color de la bomba.';
          if (step === 4) return 'Agregá al menos una bolsa para continuar.';
          return this.defaultErrorMessage;
        }

        hideExistingEditor() {
          if (this.existingEditor) {
            this.existingEditor.hidden = true;
            this.existingEditor.setAttribute('aria-hidden', 'true');
          }

          if (this.progressElement) {
            this.progressElement.hidden = false;
          }
        }

        showExistingEditor(focusTitle = false, animate = true) {
          this.pumpStage = 'quantities';
          this.goToStep(5, focusTitle, animate);
        }

        editStep(step) {
          if (step !== 3 && step !== 4) return;
          this.returnToReview = true;
          if (step === 3) this.pumpStage = 'quantities';
          this.goToStep(step);
        }

        advanceStep() {
          if (!this.isStepValid(this.currentStep)) {
            this.showError(this.getStepError(this.currentStep));
            return;
          }
          this.goToStep(this.returnToReview && this.isSelectionValid() ? 5 : Math.min(5, this.currentStep + 1));
        }

        goToStep(step, focusTitle = true, animate = true) {
          this.activeStepAnimation?.cancel();
          this.activeStepAnimation = null;
          this.hideExistingEditor();
          const nextStep = Math.min(5, Math.max(1, Number(step) || 1));

          this.currentStep = nextStep;
          if (nextStep === 5 || nextStep < 3) this.returnToReview = false;

          const incomingSection = this.stepSections.find(
            (section) => Number(section.dataset.step) === this.currentStep
          );

          this.stepSections.forEach((section) => {
            section.hidden = section !== incomingSection;
            section.setAttribute(
              'aria-hidden',
              section === incomingSection ? 'false' : 'true'
            );
          });

          if (this.progressTrack) {
            this.progressTrack.style.setProperty('--builder-progress', String(this.currentStep / 5));
            this.progressTrack.setAttribute('aria-valuenow', String(this.currentStep));
          }

          if (this.progressLabel) {
            this.progressLabel.textContent = `${String(this.currentStep).padStart(2, '0')} / 05`;
          }

          this.updateInterface();
          this.hideError();

          if (
            animate &&
            this.slideAnimationDuration > 0 &&
            incomingSection &&
            typeof incomingSection.animate === 'function'
          ) {
            const stepAnimation = incomingSection.animate(
              [
                {
                  opacity: 0
                },
                {
                  opacity: 1
                }
              ],
              {
                duration: this.slideAnimationDuration,
                easing: 'ease-out',
                fill: 'backwards'
              }
            );

            this.activeStepAnimation = stepAnimation;

            stepAnimation.addEventListener(
              'finish',
              () => {
                if (this.activeStepAnimation !== stepAnimation) return;
                stepAnimation.cancel();
                this.activeStepAnimation = null;
              },
              { once: true }
            );


          }

          if (focusTitle && incomingSection) {
            requestAnimationFrame(() => {
              if (!this.isConnected || this.currentStep !== nextStep) return;
              const stepTitle = [...incomingSection.querySelectorAll('[data-step-title]')].find(title => !title.closest('[hidden]'));
              stepTitle?.focus({ preventScroll: true });

              const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
              const titleTop = stepTitle?.getBoundingClientRect().top ?? 0;
              const comfortableTop = 96;
              if (
                isMobileViewport &&
                stepTitle &&
                (titleTop < comfortableTop || titleTop > window.innerHeight * 0.46)
              ) {
                window.scrollBy({
                  top: titleTop - comfortableTop,
                  behavior: this.prefersReducedMotion ? 'auto' : 'smooth'
                });
              }
            });
          }
        }

        shouldShowNextButton() {
          return this.currentStep === 4 || (this.currentStep === 3 && this.pumpStage === 'quantities');
        }

        updateNavigationVisibility() {
          const showBack = this.currentStep > 1;
          const showNext = this.shouldShowNextButton();
          if (this.backButton) this.backButton.hidden = !showBack;
          if (this.nextButton) {
            this.nextButton.hidden = !showNext;
            this.nextButton.textContent = this.returnToReview
              ? this.i18n.return_to_review
              : this.currentStep === 4 ? this.i18n.review_combo : this.i18n.choose_bags;
          }
          if (this.navigation) this.navigation.hidden = !showBack && !showNext;
          this.querySelector('[data-pump-question]').hidden = this.pumpStage === 'quantities';
          this.querySelector('[data-pump-quantities]').hidden = this.pumpStage !== 'quantities';
          this.querySelector('[data-saved-badge]').hidden = this.cartMode === 'new';
        }

        updateInterface() {
          this.updateNavigationVisibility();

          if (this.nextButton) {
            this.nextButton.disabled = !this.isStepValid(this.currentStep);
          }

          if (this.saveButton) {
            this.saveButton.disabled =
              !this.isSelectionValid() || this.isSaving;

            this.saveButton.textContent = this.isSaving
              ? this.savingText
              : this.cartMode === 'new'
                ? this.addText
                : this.updateText;
          }

          if (this.existingSaveButton) {
            this.existingSaveButton.disabled =
              !this.isSelectionValid() || this.isSaving;

            this.existingSaveButton.textContent = this.isSaving
              ? this.savingText
              : this.existingUpdateText;
          }

          if (this.currentStep === 5) this.updateReview();
          this.updatePricing();
          if (this.currentStep === 3) this.updateLiveSummary();
          this.persistDraft();
        }

        updateLiveSummary() {
          const name = this.querySelector('[data-selected-pump-name]');
          if (name) name.textContent = `1 ${this.i18n.pump_singular} · ${this.selectedPump?.name || ''}`;
          this.updatePumpImage('[data-selected-pump-image]');
        }

        updatePumpImage(selector, includeAll = false) {
          const target = this.querySelector(selector);
          if (!target) return;
          const ids = includeAll
            ? Object.keys(this.extraPumpQuantities).filter(id => this.getPumpTotalQuantity(id) > 0)
            : this.selectedPump ? [String(this.selectedPump.id)] : [];
          const signature = ids.join(',');
          if (target.dataset.variantIds === signature) return;
          const images = ids.map(id => this.pumpCards.find(item => item.dataset.pumpVariantId === id)?.querySelector('img')).filter(Boolean);
          target.replaceChildren(...images.map(image => image.cloneNode(true)));
          target.classList.toggle('bmh-builder__review-media--pair', images.length > 1);
          target.dataset.variantIds = signature;
        }

        updateReview() {
          const pumps = this.querySelector('[data-review-pumps]');
          const bags = this.querySelector('[data-review-bags]');
          if (!pumps || !bags) return;
          pumps.textContent = Object.entries(this.extraPumpQuantities).filter(([id]) => this.getPumpTotalQuantity(id) > 0).map(([id,data]) => {
            const count = this.getPumpTotalQuantity(id);
            return `${count} ${count === 1 ? this.i18n.pump_singular : this.i18n.pump_plural} · ${data.name}`;
          }).join('\n');
          bags.textContent = Object.values(this.bagQuantities).filter(data => data.quantity > 0).map(data => `x${data.quantity} - ${data.name}`).join('\n');
          this.updatePumpImage('[data-review-pump-image]', true);
          const chosenImages = (this.reviewBagImages || []).filter(image => this.bagQuantities[image.dataset.reviewBagVariantId]?.quantity > 0);
          (this.reviewBagImages || []).forEach(image => {
            const hidden = !chosenImages.includes(image);
            if (image.hidden !== hidden) image.hidden = hidden;
          });
          chosenImages.forEach((image, index) => {
            const offset = `${(index - (chosenImages.length - 1) / 2) * 11}px`;
            if (image.style && image.style.getPropertyValue('--bag-offset') !== offset) image.style.setProperty('--bag-offset', offset);
          });
        }

        updatePricing() {
          const lists = this.listPriceElements;
          const transfers = this.transferPriceElements;
          const savingsElements = this.savingsElements;
          const installments = this.installmentPriceElements;

          if (!this.selectedPump) {
            lists.forEach((element) => {
              element.textContent = '—';
            });
            transfers.forEach((element) => {
              element.textContent = '—';
            });
            savingsElements.forEach((element) => {
              element.textContent = '—';
            });
            installments.forEach((element) => {
              element.textContent = '—';
            });
            return;
          }

          const payment =
            this.getPaymentBreakdown(
              this.calculateTotalPrice()
            );

          const listPrice =
            this.formatMoney(payment.listPrice);
          const transferPrice =
            this.formatMoney(payment.transferPrice);
          const savings =
            this.formatMoney(payment.savings);
          const installmentPrice =
            this.formatMoney(payment.installmentPrice);

          lists.forEach((element) => {
            element.textContent = listPrice;
          });

          transfers.forEach((element) => {
            element.textContent = transferPrice;
          });

          savingsElements.forEach((element) => {
            element.textContent = savings;
          });

          installments.forEach((element) => {
            element.textContent = `${this.installmentCount}x ${installmentPrice}`;
          });
        }

        escapeHtml(value) {
          const div = document.createElement('div');
          div.textContent = String(value || '');
          return div.innerHTML;
        }

        formatMoney(cents) {
          const value = Math.round(Number(cents));
          return (value % 100 ? this.decimalMoneyFormatter : this.moneyFormatter).format(value / 100);
        }

        setLoadingState(loading) {
          if (!this.cartStatus) return;

          this.cartStatus.classList.toggle('loading', loading);
          this.cartStatus.classList.toggle('show', loading);
          if (loading) this.cartStatus.textContent = this.cartLoadingText;
        }

        showCartFoundStatus() {
          if (!this.cartStatus) return;
          this.cartStatus.textContent = this.cartFoundText;
          this.cartStatus.classList.remove('loading');
          this.cartStatus.classList.add('show');
        }

        hideCartStatus() {
          if (!this.cartStatus) return;
          this.cartStatus.classList.remove('loading', 'show');
        }

        getPumpVariantIds() {
          return new Set(this.pumpCards.map((card) => Number(card.dataset.pumpVariantId)));
        }

        getBagVariantIds() {
          return new Set(this.bagRows.map((row) => Number(row.dataset.bagVariantId)));
        }

        findExistingCartSelection(cart, preferredBundleId = null) {
          const pumpIds = this.getPumpVariantIds();
          const bagIds = this.getBagVariantIds();
          const items = Array.isArray(cart?.items) ? cart.items : [];
          const groups = new Map();

          items.forEach((item, index) => {
            const properties = item.properties || {};
            const bundleId = properties._bundle_id;
            const source = properties._bundle_source;

            if (bundleId && source === 'combo-vacio-personalizado') {
              if (!groups.has(bundleId)) {
                groups.set(bundleId, { bundleId, firstIndex: index, lines: [] });
              }
              groups.get(bundleId).lines.push(item);
            }
          });

          const validGroups = Array.from(groups.values())
            .filter((group) => {
              const hasPump = group.lines.some((item) => pumpIds.has(Number(item.variant_id)));
              const hasBag = group.lines.some((item) => bagIds.has(Number(item.variant_id)) && Number(item.quantity) > 0);
              return hasPump && hasBag;
            })
            .sort((a, b) => a.firstIndex - b.firstIndex);

          if (preferredBundleId) {
            const preferred = validGroups.find((group) => group.bundleId === preferredBundleId);
            if (preferred) return { mode: 'bundle', bundleId: preferred.bundleId, lines: preferred.lines };
          }

          if (validGroups.length) {
            const group = validGroups[0];
            return { mode: 'bundle', bundleId: group.bundleId, lines: group.lines };
          }

          const loosePumpLines = items.filter((item) => pumpIds.has(Number(item.variant_id)));
          const looseBagLines = items.filter((item) => bagIds.has(Number(item.variant_id)) && Number(item.quantity) > 0);

          if (loosePumpLines.length && looseBagLines.length) {
            return {
              mode: 'loose',
              bundleId: null,
              lines: [...loosePumpLines, ...looseBagLines]
            };
          }

          return null;
        }

        hydrateFromCart(context) {
          const pumpIds = this.getPumpVariantIds();
          const bagIds = this.getBagVariantIds();
          const pumpLines = context.lines.filter((item) =>
            pumpIds.has(Number(item.variant_id))
          );
          const pumpLine =
            pumpLines.find((item) =>
              String(item.properties?._bundle_pump_primary || '') === 'true'
            ) || pumpLines[0];

          if (!pumpLine) return false;

          const pumpCard = this.pumpCards.find(
            (card) => Number(card.dataset.pumpVariantId) === Number(pumpLine.variant_id)
          );

          if (!pumpCard) return false;
          if (pumpCard.dataset.variantAvailable === 'false') {
            this.selectedPump = { id: String(pumpLine.variant_id), price: Number(pumpCard.dataset.variantPrice), name: pumpCard.dataset.variantTitle, available: false };
          } else this.selectPump(pumpCard, false);

          Object.keys(this.extraPumpQuantities).forEach((variantId) => {
            this.setExtraPumpQuantity(variantId, 0, false);
          });

          let primaryQuantityConsumed = false;

          pumpLines.forEach((item) => {
            const variantId = String(item.variant_id);
            const existing = this.extraPumpQuantities[variantId];
            if (!existing) return;

            let extraQuantity = Math.max(0, Number(item.quantity) || 0);

            if (
              !primaryQuantityConsumed &&
              Number(item.variant_id) === Number(pumpLine.variant_id)
            ) {
              extraQuantity = Math.max(0, extraQuantity - 1);
              primaryQuantityConsumed = true;
            }

            this.setExtraPumpQuantity(
              variantId,
              existing.quantity + extraQuantity,
              false
            );
          });

          Object.keys(this.bagQuantities).forEach((variantId) => {
            this.setBagQuantity(variantId, 0, false);
          });

          context.lines.forEach((item) => {
            if (!bagIds.has(Number(item.variant_id))) return;
            const variantId = String(item.variant_id);
            const existing = this.bagQuantities[variantId];
            if (!existing) return;
            this.setBagQuantity(
              variantId,
              existing.quantity + Math.max(0, Number(item.quantity) || 0),
              false
            );
          });

          this.updateAllExtraPumpDisplays();
          this.updateAllBagDisplays();
          this.selectedPreset = this.matchCurrentPreset();
          this.journeyChoice = this.selectedPreset || 'custom';
          this.updateJourneyButtons();
          this.updatePresetPrices();

          this.cartMode = context.mode;
          this.existingBundleId = context.bundleId;
          this.existingLineKeys = context.lines.map((item) => item.key).filter(Boolean);
          return true;
        }

        async refreshComponentStock(signal) {
          const sources = [
            { url: this.dataset.pumpStockUrl, productId: this.pumpProductId, quantities: this.extraPumpQuantities, rows: [...this.pumpCards, ...(this.extraPumpRows || [])] },
            { url: this.dataset.bagStockUrl, productId: this.bagProductId, quantities: this.bagQuantities, rows: this.bagRows }
          ];

          await Promise.all(sources.map(async (source) => {
            if (!source.url) return;
            try {
              const url = new URL(source.url, window.location.origin);
              if (url.origin !== window.location.origin) return;
              url.searchParams.set('section_id', 'bmh-vacuum-stock');
              url.searchParams.set('_bmh_stock', Date.now());
              const response = await fetch(url.href, {
                headers: { Accept: 'text/html' }, cache: 'no-store', credentials: 'same-origin', signal
              });
              if (!response.ok) return;
              const html = new DOMParser().parseFromString(await response.text(), 'text/html');
              const payload = JSON.parse(html.querySelector('[data-bmh-stock]')?.textContent || 'null');
              if (!this.isConnected || signal?.aborted || String(payload?.product_id) !== String(source.productId) || !Array.isArray(payload?.variants)) return;
              const variants = new Map(payload.variants.map(variant => [String(variant.id), variant]));
              // An incomplete response must never remove a valid option or its stock cap.
              if (!Object.keys(source.quantities).every(id => {
                const variant = variants.get(id);
                return variant && typeof variant.available === 'boolean' &&
                  (variant.max === null || (Number.isInteger(variant.max) && variant.max >= 0));
              })) return;

              Object.entries(source.quantities).forEach(([id, data]) => {
                const variant = variants.get(id);
                data.available = variant.available;
                data.maxQuantity = variant.max;
                if (!data.available) data.quantity = 0;
                else if (data.maxQuantity !== null) data.quantity = Math.min(data.quantity, data.maxQuantity);
              });
              source.rows.forEach(row => {
                const id = row.dataset.pumpVariantId || row.dataset.extraPumpVariantId || row.dataset.bagVariantId;
                const data = source.quantities[id];
                if (!data) return;
                row.dataset.variantAvailable = String(data.available);
                row.dataset.maxQuantity = data.maxQuantity === null ? '' : String(data.maxQuantity);
                if (row.dataset.pumpVariantId) row.setAttribute('aria-disabled', String(!data.available));
                const status = row.querySelector('[data-stock-status]');
                if (status) status.hidden = data.available;
              });
              if (source.quantities === this.extraPumpQuantities && this.selectedPump) {
                this.selectedPump.available = this.extraPumpQuantities[this.selectedPump.id]?.available !== false;
              }
            } catch {
              // Retain the server-rendered limits offline; Shopify validates again on add.
            }
          }));
          if (!this.isConnected || signal?.aborted) return;
          this.updatePresetPrices();
          this.updateAllExtraPumpDisplays();
          this.updateAllBagDisplays();
        }

        async loadCartState() {
          if (this.cartLoadActive) return;
          this.cartLoadActive = true;
          this.isHydratingCart = true;
          const resetVersion = this.cartResetVersion || 0;
          this.setAttribute('aria-busy', 'true');
          this.setLoadingState(true);
          const draft = this.readDraft();
          const controller = new AbortController();
          const abort = () => controller.abort();
          this.lifecycle?.signal.addEventListener('abort', abort, { once: true });
          const timeout = window.setTimeout(abort, 8000);
          try {
            // Refresh tiny inventory fragments in parallel with the cart, before restoring
            // quantities. Cached product HTML can still contain the pre-restock limits.
            const [response] = await Promise.all([
              fetch(`${this.root}cart.js?_bmh=${Date.now()}`, {
                headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin', signal: controller.signal
              }),
              this.refreshComponentStock(controller.signal)
            ]);
            const cart = await this.readJsonResponse(response, this.i18n.cart_unavailable);
            if (resetVersion !== (this.cartResetVersion || 0)) return;
            if (!Array.isArray(cart?.items)) throw new Error(this.i18n.cart_unavailable);
            if (!cart.items.length) {
              this.resetAfterCartRemoval(false);
              return;
            }
            const context = this.findExistingCartSelection(cart, draft?.bundleId) || this.findEditableCartSelection(cart, draft?.bundleId);
            this.cartVerified = true;
            this.hideCartStatus();
            if (context && this.hydrateFromCart(context)) {
              this.baseFingerprint = this.contextFingerprint(context);
              this.baseSelection = this.selectionSignature();
              const stockChanged = this.contextQuantities(context) !== this.baseSelection;
              const sameCart = draft?.dirty && draft.bundleId === this.existingBundleId && draft.baseFingerprint === this.baseFingerprint;
              if (!sameCart || !this.restoreDraft(draft)) this.showExistingEditor(false, false);
              if (stockChanged) this.showError(this.i18n.stock_changed);
            } else {
              this.cartMode = 'new';
              this.existingBundleId = null;
              this.existingLineKeys = [];
              this.baseFingerprint = '';
              this.baseSelection = '';
              if (!draft || draft.cartMode !== 'new' || !this.restoreDraft(draft)) {
                this.resetSelection();
                this.goToStep(1, false, false);
              }
            }
          } catch (error) {
            if (!this.isConnected) return;
            if (resetVersion !== (this.cartResetVersion || 0)) return;
            this.cartVerified = false;
            if (draft) {
              this.cartMode = draft.cartMode || 'new';
              this.existingBundleId = draft.bundleId || null;
              this.baseFingerprint = draft.baseFingerprint || '';
              this.baseSelection = draft.baseSelection || '';
              this.restoreDraft(draft);
            }
            this.hideCartStatus();
            this.showError(this.i18n.cart_unavailable);
          } finally {
            window.clearTimeout(timeout);
            this.lifecycle?.signal.removeEventListener('abort', abort);
            this.cartLoadActive = false;
            this.isHydratingCart = false;
            this.removeAttribute('aria-busy');
            if (this.isConnected) this.updateInterface();
          }
        }

        resetSelection() {
          this.selectedPump = null;
          this.journeyChoice = null;
          this.selectedPreset = null;
          this.pumpStage = 'question';
          this.returnToReview = false;
          Object.values(this.extraPumpQuantities).forEach(data => { data.quantity = 0; });
          Object.values(this.bagQuantities).forEach(data => { data.quantity = 0; });
          this.pumpCards.forEach(card => { card.classList.remove('selected'); card.setAttribute('aria-pressed', 'false'); });
          this.updateAllBagDisplays();
          this.updateAllExtraPumpDisplays();
          this.updateJourneyButtons();
          try { window.localStorage.removeItem(this.storageKey); } catch { /* Storage can be disabled. */ }
        }

        handleCartCommitted(cart) {
          if (this.isSaving || !Array.isArray(cart?.items)) return;
          const removedBundle = this.cartMode === 'bundle' && this.existingBundleId &&
            !cart.items.some(item => item.properties?._bundle_id === this.existingBundleId);
          if (!cart.items.length || removedBundle) this.resetAfterCartRemoval();
        }

        resetAfterCartRemoval(animate = true) {
          this.cartResetVersion = (this.cartResetVersion || 0) + 1;
          window.clearTimeout(this.feedbackTimer);
          this.activeStepAnimation?.cancel();
          this.isTransitioning = false;
          this.isHydratingCart = false;
          this.removeAttribute('aria-busy');
          this.cartMode = 'new';
          this.existingBundleId = null;
          this.existingLineKeys = [];
          this.baseFingerprint = '';
          this.baseSelection = '';
          this.cartVerified = true;
          this.resetSelection();
          this.hideStockNotice();
          this.hideCartStatus();
          this.hideError();
          this.goToStep(1, false, animate);
        }

        selectionSignature() {
          const entries = Object.keys(this.extraPumpQuantities).map(id => [id, this.getPumpTotalQuantity(id)]);
          Object.entries(this.bagQuantities).forEach(([id, data]) => entries.push([id, data.quantity]));
          return JSON.stringify(entries.filter(([, qty]) => qty > 0).sort(([a], [b]) => a.localeCompare(b)));
        }

        contextQuantities(context) {
          const quantities = new Map();
          (context?.lines || []).forEach(line => {
            const id = String(line.variant_id);
            if (this.extraPumpQuantities[id] || this.bagQuantities[id]) quantities.set(id, (quantities.get(id) || 0) + Number(line.quantity));
          });
          return JSON.stringify([...quantities].filter(([, qty]) => qty > 0).sort(([a], [b]) => a.localeCompare(b)));
        }

        contextFingerprint(context) {
          return `${context?.bundleId || 'loose'}:${this.contextQuantities(context)}`;
        }

        readDraft() {
          try {
            const draft = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
            if (!draft || draft.version !== 1 || Date.now() - draft.updatedAt > 30 * 86400000 || !Number.isFinite(draft.updatedAt)) return null;
            return draft;
          } catch { return null; }
        }

        persistDraft(force = false) {
          if (!this.storageKey || this.isHydratingCart || (!force && this.isSaving) || !this.journeyChoice) return;
          const draft = {
            version: 1, updatedAt: Date.now(), step: this.currentStep, pumpStage: this.pumpStage, returnToReview: this.returnToReview,
            journey: this.journeyChoice, pump: this.selectedPump?.id || null,
            extras: Object.fromEntries(Object.entries(this.extraPumpQuantities).map(([id,data]) => [id,data.quantity])),
            bags: Object.fromEntries(Object.entries(this.bagQuantities).map(([id,data]) => [id,data.quantity])),
            cartMode: this.cartMode, bundleId: this.existingBundleId,
            baseFingerprint: this.baseFingerprint, baseSelection: this.baseSelection,
            dirty: this.selectionSignature() !== this.baseSelection
          };
          try { window.localStorage.setItem(this.storageKey, JSON.stringify(draft)); } catch { /* Cart recovery still works without local storage. */ }
        }

        restoreDraft(draft) {
          if (!draft || !['carryon','travel','home','custom'].includes(draft.journey)) return false;
          const card = this.pumpCards.find(item => item.dataset.pumpVariantId === String(draft.pump));
          this.selectedPump = null;
          Object.values(this.extraPumpQuantities).forEach(data => { data.quantity = 0; });
          Object.values(this.bagQuantities).forEach(data => { data.quantity = 0; });
          if (card?.dataset.variantAvailable !== 'false' && card) this.selectPump(card, false);
          Object.keys(this.extraPumpQuantities).forEach(id => this.setExtraPumpQuantity(id, draft.extras?.[id] || 0, false));
          Object.keys(this.bagQuantities).forEach(id => this.setBagQuantity(id, draft.bags?.[id] || 0, false));
          this.journeyChoice = draft.journey;
          this.selectedPreset = this.matchCurrentPreset();
          this.pumpStage = draft.pumpStage === 'quantities' ? 'quantities' : 'question';
          this.returnToReview = draft.returnToReview === true;
          this.updateAllExtraPumpDisplays();
          this.updateAllBagDisplays();
          this.updateJourneyButtons();
          let step = Math.min(5, Math.max(1, Number(draft.step) || 1));
          if (!this.selectedPump && step > 2) step = 2;
          else if (!this.isSelectionValid() && step === 5) step = 4;
          this.goToStep(step, false, false);
          return true;
        }

        createBundleId() {
          if (window.crypto?.randomUUID) {
            return `BMH-${window.crypto.randomUUID()}`;
          }

          return `BMH-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        }

        buildDesiredItems(bundleId) {
          const commonProperties = {
            _bundle_id: bundleId,
            _bundle_source: 'combo-vacio-personalizado',
            _bundle_name: 'Combo Vacío Personalizado',
            _bundle_parent_product_id: this.publicProductId,
            _bundle_preset: this.selectedPreset || 'personalizado'
          };

          const items = [];

          Object.entries(this.extraPumpQuantities).forEach(([variantId, data]) => {
            const isPrimary =
              String(this.selectedPump.id) === String(variantId);
            const quantity =
              Number(data.quantity) + (isPrimary ? 1 : 0);

            if (quantity < 1) return;

            items.push({
              id: Number(variantId),
              quantity,
              properties: {
                ...commonProperties,
                _bundle_role: 'pump',
                _bundle_pump_color: data.name,
                _bundle_pump_primary: isPrimary ? 'true' : 'false'
              }
            });
          });

          if (!items.length) {
            items.push({
              id: Number(this.selectedPump.id),
              quantity: 1,
              properties: {
                ...commonProperties,
                _bundle_role: 'pump',
                _bundle_pump_color: this.selectedPump.name,
                _bundle_pump_primary: 'true'
              }
            });
          }

          Object.entries(this.bagQuantities).forEach(([variantId, data]) => {
            if (data.quantity < 1) return;

            items.push({
              id: Number(variantId),
              quantity: Number(data.quantity),
              properties: {
                ...commonProperties,
                _bundle_role: 'bag',
                _bundle_size: data.name
              }
            });
          });

          return items;
        }

        getDesiredQuantities() {
          const quantities = new Map();

          Object.entries(this.extraPumpQuantities).forEach(([variantId, data]) => {
            const quantity =
              Number(data.quantity) +
              (String(this.selectedPump.id) === String(variantId) ? 1 : 0);

            if (quantity > 0) {
              quantities.set(Number(variantId), quantity);
            }
          });

          if (!quantities.has(Number(this.selectedPump.id))) {
            quantities.set(Number(this.selectedPump.id), 1);
          }

          Object.entries(this.bagQuantities).forEach(([variantId, data]) => {
            if (data.quantity > 0) {
              quantities.set(Number(variantId), Number(data.quantity));
            }
          });

          return quantities;
        }

        async readJsonResponse(response, fallbackMessage) {
          let result = null;

          try {
            result = await response.json();
          } catch (error) {
            result = null;
          }

          if (!response.ok) {
            const message =
              result?.description ||
              result?.message ||
              fallbackMessage;

            const requestError = new Error(message);
            requestError.status = response.status;
            requestError.payload = result;

            throw requestError;
          }

          return result;
        }

        async fetchCart() {
          const separator =
            String(this.root).includes('?') ? '&' : '?';

          const response = await fetch(
            `${this.root}cart.js${separator}_bmh=${Date.now()}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json'
              },
              cache: 'no-store',
              credentials: 'same-origin'
            }
          );

          return this.readJsonResponse(
            response,
            'No pudimos leer el carrito.'
          );
        }

        async addItems(items) {
          if (!Array.isArray(items) || !items.length) {
            return null;
          }

          const response = await fetch(`${this.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({ items })
          });

          const result = await this.readJsonResponse(
            response,
            'No pudimos agregar el combo al carrito.'
          );

          const addedLines = Array.isArray(result)
            ? result
            : Array.isArray(result?.items)
              ? result.items
              : [];

          if (!addedLines.length) {
            throw new Error(
              'Shopify no confirmó los productos agregados. Intentá nuevamente.'
            );
          }

          return result;
        }

        async changeCartLine(lineKey, quantity, properties = null) {
          if (!lineKey) {
            throw new Error(
              'No pudimos identificar uno de los productos del carrito.'
            );
          }

          const payload = {
            id: String(lineKey),
            quantity: Math.max(
              0,
              Number.parseInt(quantity, 10) || 0
            )
          };

          if (properties && payload.quantity > 0) {
            payload.properties = properties;
          }

          const response = await fetch(`${this.root}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify(payload)
          });

          return this.readJsonResponse(
            response,
            'No pudimos modificar uno de los productos del carrito.'
          );
        }

        getBundleLines(cart, bundleId) {
          if (!bundleId) return [];

          return (Array.isArray(cart?.items) ? cart.items : [])
            .filter((item) => {
              const properties = item.properties || {};

              return (
                String(properties._bundle_id || '') ===
                  String(bundleId) &&
                properties._bundle_source ===
                  'combo-vacio-personalizado'
              );
            });
        }

        getLooseCompatibleLines(cart) {
          const pumpIds = this.getPumpVariantIds();
          const bagIds = this.getBagVariantIds();

          return (Array.isArray(cart?.items) ? cart.items : [])
            .filter((item) => {
              const properties = item.properties || {};
              const belongsToBundle =
                properties._bundle_source ===
                'combo-vacio-personalizado';

              if (belongsToBundle) return false;

              const variantId = Number(item.variant_id);

              return (
                pumpIds.has(variantId) ||
                bagIds.has(variantId)
              );
            });
        }

        getCurrentCompatibleLines(cart) {
          const pumpIds = this.getPumpVariantIds();
          const bagIds = this.getBagVariantIds();

          return (Array.isArray(cart?.items) ? cart.items : [])
            .filter((item) => {
              const variantId = Number(item.variant_id);

              return (
                pumpIds.has(variantId) ||
                bagIds.has(variantId)
              );
            });
        }

        normalizeCartText(value) {
          return String(value || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        }

        isComboRelatedLine(item) {
          if (!item) return false;

          const properties = item.properties || {};
          const variantId = Number(item.variant_id);
          const productId = Number(item.product_id);
          const pumpIds = this.getPumpVariantIds();
          const bagIds = this.getBagVariantIds();

          const isCurrentVariant =
            pumpIds.has(variantId) ||
            bagIds.has(variantId);

          const isCurrentInternalProduct =
            (
              this.pumpProductId > 0 &&
              productId === this.pumpProductId
            ) ||
            (
              this.bagProductId > 0 &&
              productId === this.bagProductId
            );

          const isMarkedBundle =
            properties._bundle_source ===
              'combo-vacio-personalizado' ||
            String(
              properties._bundle_parent_product_id || ''
            ) === String(this.publicProductId || '') ||
            (
              properties._bundle_id &&
              (
                properties._bundle_role === 'pump' ||
                properties._bundle_role === 'bag'
              )
            );

          const title = this.normalizeCartText(
            item.product_title ||
            item.title ||
            item.product?.title
          );

          const isLegacyInternalTitle =
            (
              title.includes('combo') &&
              title.includes('vacio') &&
              (
                title.includes('bolsas') ||
                title.includes('bolsos') ||
                title.includes('bomba')
              )
            ) ||
            title.includes(
              'bomba inalambrica para bolsas al vacio combo'
            ) ||
            title.includes('bolsas al vacio combo') ||
            title.includes('bolsos al vacio combo');

          return (
            isCurrentVariant ||
            isCurrentInternalProduct ||
            isMarkedBundle ||
            isLegacyInternalTitle
          );
        }

        getCompatibleLines(cart) {
          return (Array.isArray(cart?.items) ? cart.items : [])
            .filter((item) => this.isComboRelatedLine(item));
        }

        findEditableCartSelection(
          cart,
          preferredBundleId = null
        ) {
          const compatibleLines =
            this.getCurrentCompatibleLines(cart);

          if (!compatibleLines.length) {
            return null;
          }

          if (preferredBundleId) {
            const preferredLines =
              compatibleLines.filter((item) => {
                const properties = item.properties || {};

                return (
                  String(properties._bundle_id || '') ===
                  String(preferredBundleId)
                );
              });

            if (preferredLines.length) {
              return {
                mode: 'bundle',
                bundleId: String(preferredBundleId),
                lines: preferredLines
              };
            }
          }

          const groupedBundles = new Map();

          compatibleLines.forEach((item, index) => {
            const properties = item.properties || {};
            const bundleId = properties._bundle_id;
            const source = properties._bundle_source;

            if (
              !bundleId ||
              source !== 'combo-vacio-personalizado'
            ) {
              return;
            }

            if (!groupedBundles.has(String(bundleId))) {
              groupedBundles.set(String(bundleId), {
                bundleId: String(bundleId),
                firstIndex: index,
                lines: []
              });
            }

            groupedBundles
              .get(String(bundleId))
              .lines.push(item);
          });

          const firstBundle =
            Array.from(groupedBundles.values())
              .sort(
                (first, second) =>
                  first.firstIndex - second.firstIndex
              )[0];

          if (firstBundle?.lines?.length) {
            return {
              mode: 'bundle',
              bundleId: firstBundle.bundleId,
              lines: firstBundle.lines
            };
          }

          const looseLines =
            this.getLooseCompatibleLines(cart);

          if (looseLines.length) {
            return {
              mode: 'loose',
              bundleId: null,
              lines: looseLines
            };
          }

          return null;
        }

        getEditableLines(
          cart,
          bundleId,
          originalLineKeys
        ) {
          const keySet =
            originalLineKeys instanceof Set
              ? originalLineKeys
              : new Set();

          return (
            Array.isArray(cart?.items) ? cart.items : []
          ).filter((item) => {
            const properties = item.properties || {};

            const belongsToCurrentBundle =
              bundleId &&
              String(properties._bundle_id || '') ===
                String(bundleId) &&
              properties._bundle_source ===
                'combo-vacio-personalizado';

            const isOriginalLooseLine =
              keySet.has(String(item.key));

            return (
              belongsToCurrentBundle ||
              isOriginalLooseLine
            );
          });
        }

        async removeLinesByKeys(cart, keys) {
          let currentCart = cart;
          const pendingKeys = new Set((keys || []).filter(Boolean).map(String));

          // One targeted update replaces a request for every component. Keep the
          // existing per-line fallback for stale keys or rejected batch updates.
          const updates = Object.fromEntries(
            (cart?.items || []).filter(item => pendingKeys.has(String(item.key))).map(item => [item.key, 0])
          );
          if (Object.keys(updates).length) {
            try {
              const response = await fetch(`${this.root}cart/update.js`, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ updates })
              });
              currentCart = await this.readJsonResponse(response, this.defaultErrorMessage);
            } catch {
              currentCart = await this.fetchCart();
            }
          }
          while (pendingKeys.size) {
            const currentLine = (
              Array.isArray(currentCart?.items)
                ? currentCart.items
                : []
            ).find((item) => pendingKeys.has(String(item.key)));

            if (!currentLine) {
              break;
            }

            pendingKeys.delete(String(currentLine.key));

            currentCart = await this.changeCartLine(
              currentLine.key,
              0
            );
          }

          return currentCart;
        }

        snapshotCompatibleItems(cart) {
          return this.getCurrentCompatibleLines(cart)
            .filter((line) => Number(line.quantity) > 0)
            .map((line) => {
              const item = {
                id: Number(line.variant_id),
                quantity: Number(line.quantity)
              };

              const properties = line.properties || {};
              if (Object.keys(properties).length) {
                item.properties = { ...properties };
              }

              const sellingPlan =
                line.selling_plan_allocation?.selling_plan?.id ||
                line.selling_plan;

              if (sellingPlan) {
                item.selling_plan = Number(sellingPlan);
              }

              return item;
            });
        }

        sleep(milliseconds) {
          return new Promise((resolve) => {
            window.setTimeout(
              resolve,
              Math.max(0, Number(milliseconds) || 0)
            );
          });
        }

        async clearCompatibleLines(cart) {
          const compatibleKeys =
            this.getCompatibleLines(cart)
              .map((line) => String(line.key || ''))
              .filter(Boolean);

          if (!compatibleKeys.length) {
            return cart;
          }

          const updates = {};

          compatibleKeys.forEach((key) => {
            updates[key] = 0;
          });

          const response = await fetch(
            `${this.root}cart/update.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              credentials: 'same-origin',
              body: JSON.stringify({ updates })
            }
          );

          try {
            return await this.readJsonResponse(
              response,
              'No pudimos limpiar el combo anterior del carrito.'
            );
          } catch (error) {
            let currentCart = await this.fetchCart();
            let safetyCounter = 0;

            while (safetyCounter < 30) {
              const compatibleLines =
                this.getCompatibleLines(currentCart);

              if (!compatibleLines.length) {
                return currentCart;
              }

              const lineToRemove =
                compatibleLines[compatibleLines.length - 1];

              currentCart = await this.changeCartLine(
                lineToRemove.key,
                0
              );

              safetyCounter += 1;
            }

            throw new Error(
              'No pudimos limpiar por completo el combo anterior.'
            );
          }
        }

        async clearEntireCart() {
          const response = await fetch(
            `${this.root}cart/clear.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              credentials: 'same-origin',
              body: JSON.stringify({})
            }
          );

          return this.readJsonResponse(
            response,
            'No pudimos reiniciar el carrito.'
          );
        }

        getBundleQuantity(cart, variantId, bundleId) {
          return (
            Array.isArray(cart?.items) ? cart.items : []
          ).reduce((total, line) => {
            const properties = line.properties || {};
            const matchesVariant =
              Number(line.variant_id) === Number(variantId);
            const matchesBundle =
              String(properties._bundle_id || '') ===
                String(bundleId || '');

            return matchesVariant && matchesBundle
              ? total + Math.max(0, Number(line.quantity) || 0)
              : total;
          }, 0);
        }

        async removeBundleVariantLines(
          cart,
          variantId,
          bundleId
        ) {
          let currentCart = cart;
          let safetyCounter = 0;

          while (safetyCounter < 10) {
            const line = (
              Array.isArray(currentCart?.items)
                ? currentCart.items
                : []
            ).find((item) => {
              const properties = item.properties || {};

              return (
                Number(item.variant_id) === Number(variantId) &&
                String(properties._bundle_id || '') ===
                  String(bundleId || '')
              );
            });

            if (!line) return currentCart;

            currentCart = await this.changeCartLine(
              line.key,
              0
            );

            safetyCounter += 1;
          }

          return currentCart;
        }

        describeDesiredItem(item) {
          const properties = item?.properties || {};

          if (properties._bundle_role === 'pump') {
            return `la bomba ${properties._bundle_pump_color || ''}`.trim();
          }

          if (properties._bundle_role === 'bag') {
            return `las bolsas ${properties._bundle_size || ''}`.trim();
          }

          return 'uno de los productos';
        }

        async addItemWithRetry(
          item,
          bundleId,
          maxAttempts = 3
        ) {
          let lastError = null;

          for (
            let attempt = 1;
            attempt <= maxAttempts;
            attempt += 1
          ) {
            try {
              await this.addItems([item]);
              return;
            } catch (error) {
              lastError = error;

              if (Number(error?.status) !== 422) {
                throw error;
              }

              const cartAfterError =
                await this.fetchCart();

              const quantityInCart =
                this.getBundleQuantity(
                  cartAfterError,
                  item.id,
                  bundleId
                );

              if (quantityInCart >= Number(item.quantity)) {
                return;
              }

              await this.removeBundleVariantLines(
                cartAfterError,
                item.id,
                bundleId
              );

              if (attempt < maxAttempts) {
                await this.sleep(650 * attempt);
              }
            }
          }

          if (lastError) {
            lastError.failedItemName =
              this.describeDesiredItem(item);
            lastError.failedVariantId =
              Number(item.id);
          }

          throw lastError || new Error(
            'No pudimos agregar uno de los productos.'
          );
        }

        async addItemsSequentially(items, bundleId) {
          for (const item of items) {
            await this.addItemWithRetry(
              item,
              bundleId,
              3
            );
          }
        }

        async addSelectionWithFallback(items, bundleId) {
          try {
            await this.addItems(items);
            return;
          } catch (error) {
            if (Number(error?.status) !== 422) {
              throw error;
            }

            const partialCart =
              await this.fetchCart();

            await this.clearBundleById(partialCart, bundleId);

            await this.sleep(700);

            await this.addItemsSequentially(
              items,
              bundleId
            );
          }
        }

        async addNewSelectionFast() {
          const bundleId = this.createBundleId();
          const desiredItems =
            this.buildDesiredItems(bundleId);

          try {
            await this.addItems(desiredItems);
          } catch (error) {
            if (Number(error?.status) !== 422) {
              throw error;
            }

            const partialCart = await this.fetchCart();
            const partialLines =
              this.getBundleLines(partialCart, bundleId);

            if (partialLines.length) {
              await this.removeLinesByKeys(
                partialCart,
                partialLines.map((line) => line.key)
              );
            }

            await this.addItemsSequentially(
              desiredItems,
              bundleId
            );
          }

          this.existingBundleId = bundleId;
          this.cartMode = 'bundle';
        }

        async restoreCompatibleItems(items) {
          if (!Array.isArray(items) || !items.length) {
            return;
          }

          const bundleId =
            items[0]?.properties?._bundle_id ||
            this.createBundleId();

          try {
            await this.addSelectionWithFallback(
              items,
              bundleId
            );
          } catch (error) {
            console.error(
              'No se pudo restaurar el combo anterior:',
              error
            );
          }
        }

        snapshotLines(lines) {
          return (Array.isArray(lines) ? lines : [])
            .filter((line) => Number(line.quantity) > 0)
            .map((line) => {
              const item = {
                id: Number(line.variant_id),
                quantity: Number(line.quantity)
              };

              const properties = line.properties || {};
              if (Object.keys(properties).length) {
                item.properties = { ...properties };
              }

              const sellingPlan =
                line.selling_plan_allocation?.selling_plan?.id ||
                line.selling_plan;

              if (sellingPlan) {
                item.selling_plan = Number(sellingPlan);
              }

              return item;
            });
        }

        getEditingContext(cart) {
          const items = Array.isArray(cart?.items) ? cart.items : [];

          if (this.existingBundleId) {
            const bundleLines = items.filter((item) => {
              const properties = item.properties || {};

              return (
                String(properties._bundle_id || '') ===
                  String(this.existingBundleId) &&
                properties._bundle_source ===
                  'combo-vacio-personalizado'
              );
            });

            if (bundleLines.length) {
              return {
                mode: 'bundle',
                bundleId: String(this.existingBundleId),
                lines: bundleLines
              };
            }
          }

          if (this.existingLineKeys.length) {
            const keySet = new Set(
              this.existingLineKeys.map(String)
            );

            const originalLines = items.filter((item) =>
              keySet.has(String(item.key))
            );

            if (originalLines.length) {
              return {
                mode: this.cartMode || 'loose',
                bundleId: this.existingBundleId,
                lines: originalLines
              };
            }
          }

          if (this.existingBundleId) return null;
          return this.findEditableCartSelection(cart);
        }

        async clearEditingContext(cart, context = null) {
          const resolvedContext =
            context || this.getEditingContext(cart);

          if (!resolvedContext?.lines?.length) {
            return cart;
          }

          return this.removeLinesByKeys(
            cart,
            resolvedContext.lines
              .map((line) => line.key)
              .filter(Boolean)
          );
        }

        async clearBundleById(cart, bundleId) {
          if (!bundleId) return cart;

          const lines = this.getBundleLines(
            cart,
            bundleId
          );

          if (!lines.length) return cart;

          return this.removeLinesByKeys(
            cart,
            lines.map((line) => line.key).filter(Boolean)
          );
        }

        async rebuildSelection(verifiedCart = null) {
          const initialCart = verifiedCart || await this.fetchCart();
          const originalContext = this.getEditingContext(
            initialCart
          );

          const previousItems = this.snapshotLines(
            originalContext?.lines || []
          );

          const bundleId =
            this.existingBundleId ||
            this.createBundleId();

          const desiredItems =
            this.buildDesiredItems(bundleId);

          /*
           * Solo quitamos las líneas que pertenecen al combo que el
           * usuario está editando. Así cambiar la cantidad de bolsas no
           * toca otros productos compatibles que pudiera tener en el carrito.
           */
          await this.clearEditingContext(
            initialCart,
            originalContext
          );

          try {
            await this.addSelectionWithFallback(
              desiredItems,
              bundleId
            );
          } catch (error) {
            try {
              const cartAfterFailure =
                await this.fetchCart();

              await this.clearBundleById(
                cartAfterFailure,
                bundleId
              );

              await this.restoreCompatibleItems(
                previousItems
              );
            } catch (restoreError) {
              console.error(
                'No se pudo restaurar el combo anterior:',
                restoreError
              );
            }

            throw error;
          }

          this.existingBundleId = bundleId;
          this.cartMode = 'bundle';
          this.existingLineKeys = [];
        }

        async persistSelectionWithInventoryRetry(verifiedCart = null) {
          if (
            this.cartMode === 'new' &&
            !this.existingBundleId &&
            !this.existingLineKeys.length
          ) {
            await this.addNewSelectionFast();
            return;
          }

          try {
            await this.rebuildSelection(verifiedCart);
          } catch (error) {
            if (Number(error?.status) !== 422) {
              throw error;
            }

            const refreshedCart =
              await this.fetchCart();

            const bundleId =
              this.existingBundleId ||
              this.createBundleId();

            /*
             * El reintento también es quirúrgico: elimina únicamente el
             * combo que estamos editando o cualquier agregado parcial del
             * mismo bundle. Nunca vacía el carrito completo.
             */
            const retryContext =
              this.getEditingContext(refreshedCart);

            let cartAfterCleanup =
              await this.clearEditingContext(
                refreshedCart,
                retryContext
              );

            cartAfterCleanup =
              await this.clearBundleById(
                cartAfterCleanup,
                bundleId
              );

            await this.sleep(700);

            const desiredItems =
              this.buildDesiredItems(bundleId);

            await this.addItemsSequentially(
              desiredItems,
              bundleId
            );

            this.existingBundleId = bundleId;
            this.cartMode = 'bundle';
            this.existingLineKeys = [];
          }
        }

        dispatchCartUpdated() {
          const detail = {
            source: 'combo-vacio-personalizado'
          };

          document.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail
            })
          );

          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail
            })
          );
        }

        showError(message) {
          if (!this.errorElement) return;
          this.errorElement.textContent = message || this.defaultErrorMessage;
          this.errorElement.classList.add('show');
        }

        hideError() {
          this.errorElement?.classList.remove('show');
        }

        disconnectedCallback() {
          this.persistDraft();
          this.lifecycle?.abort();
          window.clearTimeout(this.feedbackTimer);
          this.activeStepAnimation?.cancel();
          this.initialized = false;
          this.isTransitioning = false;
        }

        async saveSelection() {
          if (!this.isSelectionValid() || this.isSaving) {
            this.showError(this.defaultErrorMessage);
            return;
          }

          this.isSaving = true;
          this.hideError();
          this.updateInterface();

          try {
            const currentCart = await this.fetchCart();
            if (this.cartMode === 'new') {
              const context = this.findExistingCartSelection(currentCart) || this.findEditableCartSelection(currentCart);
              if (context) {
                this.cartMode = context.mode;
                this.existingBundleId = context.bundleId;
                this.existingLineKeys = context.lines.map(line => line.key);
              }
            }
            await this.persistSelectionWithInventoryRetry(currentCart);
            this.currentStep = 5;
            this.baseSelection = this.selectionSignature();
            this.baseFingerprint = `${this.existingBundleId || 'loose'}:${this.baseSelection}`;
            this.persistDraft(true);
            window.location.assign(`${this.root}cart`);
          } catch (error) {
            console.error(
              'Error al guardar el combo:',
              {
                status: error?.status,
                message: error?.message,
                payload: error?.payload,
                failedItemName: error?.failedItemName,
                failedVariantId: error?.failedVariantId
              }
            );

            this.isSaving = false;
            this.updateInterface();

            const isInventoryError =
              Number(error?.status) === 422;

            const failedItem =
              error?.failedItemName || '';

            const inventoryMessage = failedItem
              ? `No pudimos agregar ${failedItem}. Tocá nuevamente el botón para reintentar.`
              : 'No pudimos agregar una de las opciones seleccionadas. Tocá nuevamente el botón para reintentar.';

            this.showError(
              isInventoryError
                ? inventoryMessage
                : (
                    error?.message ||
                    'No pudimos guardar el combo. Intentá nuevamente.'
                  )
            );
          }
        }
      }
    );
  
};

const initWizards = (scope = document) => {
  const selector = '[data-bmh-vacuum-wizard]';
  const wizards = [...scope.querySelectorAll(selector)];
  if (scope.matches?.(selector)) wizards.unshift(scope);
  wizards.forEach((wizard) => {
    if (wizard.localName.startsWith('vacuum-bundle-wizard-')) {
      registerWizard(wizard.localName);
    }
  });
};

initWizards();
document.addEventListener('shopify:section:load', (event) => initWizards(event.target));
